/**
 * CommunicationLog — Becky-T2.1 (issue #33).
 *
 * Renders the chronological touchpoint feed for a customer (phone /
 * email / SMS / in-app / in-person) and provides a compact form to log
 * a new entry. Optional `dogId` scopes the feed to a single pet so the
 * same component renders the pet-detail tab without forking.
 *
 * The parent (CustomerDetail) is responsible for the auth token + API
 * URL — this component is purely the staffer's lens onto a customer's
 * communication history.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Alert, Button } from '@ferro/ui';
import {
  COMMUNICATION_KINDS,
  createCommunication,
  fetchCommunications,
  type CommunicationKind,
  type CommunicationLogEntry,
} from '../../lib/api.js';

const KIND_LABELS: Record<CommunicationKind, string> = {
  phone: 'Phone',
  email: 'Email',
  sms: 'SMS',
  in_app: 'In-app',
  in_person: 'In person',
};

export interface CommunicationLogProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly customerId: string;
  // When set, only entries for this dog are shown and any new entry is
  // automatically tagged with the dog id.
  readonly dogId?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; entries: CommunicationLogEntry[] };

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function CommunicationLog({
  apiUrl,
  token,
  customerId,
  dogId,
}: CommunicationLogProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [kind, setKind] = useState<CommunicationKind>('phone');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const res = await fetchCommunications(apiUrl, token, customerId, {
        dogId,
        limit: 100,
      });
      setState({ kind: 'ready', entries: res.communications });
    } catch (err) {
      setState({
        kind: 'error',
        message:
          err instanceof Error ? err.message : 'Unable to load communications',
      });
    }
  }, [apiUrl, token, customerId, dogId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;

    const trimmedReason = reason.trim();
    const trimmedNotes = notes.trim();
    if (trimmedReason.length === 0 && trimmedNotes.length === 0) {
      setSubmitError('Add a reason or note to log this touchpoint.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    // Optimistic insert at the top of the list so the staffer gets the
    // "logged by Becky" attribution immediately (AC2).
    const optimistic: CommunicationLogEntry = {
      id: `optimistic-${Date.now()}`,
      customer_id: customerId,
      dog_id: dogId ?? null,
      dog_name: null,
      staff_id: null,
      staff_name: 'You',
      kind,
      reason: trimmedReason || null,
      notes: trimmedNotes || null,
      occurred_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
    };
    const previous = state;
    setState((current) => {
      if (current.kind !== 'ready') {
        return { kind: 'ready', entries: [optimistic] };
      }
      return { ...current, entries: [optimistic, ...current.entries] };
    });
    setReason('');
    setNotes('');

    try {
      const res = await createCommunication(apiUrl, token, customerId, {
        kind,
        reason: trimmedReason || undefined,
        notes: trimmedNotes || undefined,
        dog_id: dogId ?? null,
      });
      setState((current) => {
        if (current.kind !== 'ready') return current;
        return {
          ...current,
          entries: current.entries.map((e) =>
            e.id === optimistic.id ? res.communication : e,
          ),
        };
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to log communication',
      );
      setState(previous);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article
      className="ferro-dashboard__card"
      aria-label="Communication log"
      data-testid="communication-log"
    >
      <header className="ferro-dashboard__cardHeader">
        <h2 className="ferro-dashboard__cardTitle">
          {dogId ? 'Pet communication log' : 'Communication log'}
        </h2>
        {state.kind === 'ready' ? (
          <span className="ferro-dashboard__metric">{state.entries.length}</span>
        ) : null}
      </header>

      <form className="ferro-form" onSubmit={handleSubmit} noValidate>
        <div className="ferro-form__row">
          <label className="ferro-field">
            <span className="ferro-field__label">Channel</span>
            <select
              className="ferro-field__input"
              value={kind}
              onChange={(e) => setKind(e.target.value as CommunicationKind)}
              data-testid="communication-kind"
            >
              {COMMUNICATION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Reason</span>
            <input
              type="text"
              className="ferro-field__input"
              placeholder="Vaccine reminder, boarding question…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="communication-reason"
            />
          </label>
        </div>
        <label className="ferro-field">
          <span className="ferro-field__label">Notes</span>
          <textarea
            className="ferro-field__input ferro-field__input--textarea"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="communication-notes"
          />
        </label>

        {submitError ? <Alert variant="error">{submitError}</Alert> : null}

        <div className="ferro-form__actions">
          <Button
            variant="primary"
            type="submit"
            disabled={submitting}
            data-testid="communication-submit"
          >
            {submitting ? 'Logging…' : 'Log touchpoint'}
          </Button>
        </div>
      </form>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status" data-testid="comm-log-loading">
          Loading communications…
        </p>
      ) : null}

      {/* #242 — graceful error: ferro-alert (dismissible) + retry. The
          previous ferro-dashboard__error div had no testid and no Retry
          visible on the error path in the Communication-log tab. */}
      {state.kind === 'error' ? (
        <div
          className="ferro-alert ferro-alert--error ferro-alert--dismissible"
          role="alert"
          data-testid="comm-log-error"
        >
          <span>{state.message}</span>
          <button
            type="button"
            className="ferro-alert__dismiss"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        state.entries.length === 0 ? (
          <p
            className="ferro-muted"
            data-testid="comm-log-empty"
          >
            No communications logged yet. Use the form above to log the first touchpoint.
          </p>
        ) : (
          <ul
            className="ferro-timeline"
            aria-label="Communication timeline"
            data-testid="communication-timeline"
          >
            {state.entries.map((entry) => (
              <li key={entry.id} className="ferro-timeline__item">
                <span className="ferro-timeline__when">
                  {formatWhen(entry.occurred_at)} ·{' '}
                  <strong>{KIND_LABELS[entry.kind]}</strong>
                  {entry.staff_name ? ` · logged by ${entry.staff_name}` : ''}
                  {entry.dog_name && !dogId ? ` · re: ${entry.dog_name}` : ''}
                </span>
                <span className="ferro-timeline__what">
                  {entry.reason ? <strong>{entry.reason}</strong> : null}
                  {entry.reason && entry.notes ? ' — ' : null}
                  {entry.notes}
                </span>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </article>
  );
}
